/* ============================================================
   AL SAFYR — JAVASCRIPT UNIFIÉ
   Tous les modules du thème en un fichier
   ============================================================ */

(function () {
  'use strict';

  /* ============================================================
     UTILITAIRES
     ============================================================ */
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];
  const on = (el, ev, fn, opts) => el && el.addEventListener(ev, fn, opts);
  const formatMoney = (cents) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(cents / 100);
  };

  /* ============================================================
     CURSOR GLOW
     ============================================================ */
  const cursor = $('#as-cursor-glow');
  if (cursor && window.matchMedia('(hover: hover)').matches) {
    on(document, 'mousemove', (e) => {
      cursor.style.left = e.clientX + 'px';
      cursor.style.top = e.clientY + 'px';
    });
  }

  /* ============================================================
     HEADER SCROLL
     ============================================================ */
  const header = $('#as-header');
  if (header) {
    const onScroll = () => {
      header.classList.toggle('is-scrolled', window.scrollY > 50);
    };
    on(window, 'scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ============================================================
     HAMBURGER / MOBILE MENU
     ============================================================ */
  const hamburger = $('#as-hamburger');
  const mobMenu = $('#as-mob-menu');
  const mobClose = $('#as-mob-close');
  const mobBackdrop = $('#as-mob-backdrop');

  function openMobMenu() {
    if (!mobMenu) return;
    mobMenu.hidden = false;
    mobMenu.removeAttribute('hidden');
    requestAnimationFrame(() => mobMenu.classList.add('is-open'));
    hamburger && hamburger.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
  }

  function closeMobMenu() {
    if (!mobMenu) return;
    mobMenu.classList.remove('is-open');
    hamburger && hamburger.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
    setTimeout(() => { mobMenu.hidden = true; }, 350);
  }

  on(hamburger, 'click', openMobMenu);
  on(mobClose, 'click', closeMobMenu);
  on(mobBackdrop, 'click', closeMobMenu);

  /* ============================================================
     SEARCH OVERLAY
     ============================================================ */
  const searchOpen = $('#as-search-open');
  const searchOverlay = $('#as-search-overlay');
  const searchClose = $('#as-search-close');
  const searchBackdrop = $('#as-search-backdrop');
  const searchInput = searchOverlay && $('.as-search-overlay__input', searchOverlay);

  function openSearch() {
    if (!searchOverlay) return;
    searchOverlay.hidden = false;
    requestAnimationFrame(() => {
      searchOverlay.removeAttribute('hidden');
      searchInput && setTimeout(() => searchInput.focus(), 100);
    });
    document.body.style.overflow = 'hidden';
  }

  function closeSearch() {
    if (!searchOverlay) return;
    searchOverlay.hidden = true;
    document.body.style.overflow = '';
  }

  on(searchOpen, 'click', openSearch);
  on(searchClose, 'click', closeSearch);
  on(searchBackdrop, 'click', closeSearch);
  on(document, 'keydown', (e) => {
    if (e.key === 'Escape') {
      closeSearch();
      closeMobMenu();
      closeCartDrawer();
    }
  });

  /* ============================================================
     CART DRAWER
     ============================================================ */
  const cartDrawer = $('#as-cart-drawer');
  const cartOpen = $('#as-cart-open');
  const cartClose = $('#as-cart-close');
  const cartOverlay = $('#as-cart-overlay');
  const cartBadge = $('#as-cart-badge');
  const drawerCount = $('#as-drawer-count');
  const cartItems = $('#as-cart-items');
  const cartEmpty = $('#as-cart-empty');
  const cartFooter = $('#as-cart-footer');
  const cartSubtotal = $('#as-cart-subtotal');
  const shippingText = $('#as-shipping-text');
  const shippingFill = $('#as-shipping-fill');
  const toast = $('#as-toast');
  const toastMsg = $('#as-toast-msg');

  const FREE_SHIPPING_THRESHOLD = 5000; // centimes = 50€

  function openCartDrawer() {
    if (!cartDrawer) return;
    cartDrawer.hidden = false;
    requestAnimationFrame(() => cartDrawer.removeAttribute('hidden'));
    document.body.style.overflow = 'hidden';
    fetchAndRenderCart();
  }

  function closeCartDrawer() {
    if (!cartDrawer) return;
    cartDrawer.hidden = true;
    document.body.style.overflow = '';
  }

  on(cartOpen, 'click', openCartDrawer);
  on(cartClose, 'click', closeCartDrawer);
  on(cartOverlay, 'click', closeCartDrawer);

  function showToast(msg, duration = 3000) {
    if (!toast || !toastMsg) return;
    toastMsg.textContent = msg;
    toast.hidden = false;
    clearTimeout(toast._timer);
    toast._timer = setTimeout(() => { toast.hidden = true; }, duration);
  }

  function updateCartBadge(count) {
    if (!cartBadge) return;
    if (count > 0) {
      cartBadge.style.display = '';
      cartBadge.textContent = count;
      cartBadge.classList.add('is-bump');
      setTimeout(() => cartBadge.classList.remove('is-bump'), 400);
    } else {
      cartBadge.style.display = 'none';
    }
  }

  function updateShippingBar(totalCents) {
    if (!shippingText || !shippingFill) return;
    const remaining = FREE_SHIPPING_THRESHOLD - totalCents;
    const progress = Math.min((totalCents / FREE_SHIPPING_THRESHOLD) * 100, 100);
    shippingFill.style.width = progress + '%';
    if (remaining > 0) {
      shippingText.textContent = `Plus que ${formatMoney(remaining)} pour la livraison offerte`;
    } else {
      shippingText.textContent = '🎉 Livraison offerte débloquée !';
    }
  }

  function renderCartItems(cart) {
    if (!cartItems) return;
    if (cart.item_count === 0) {
      cartItems.innerHTML = '';
      cartEmpty && (cartEmpty.hidden = false);
      cartFooter && (cartFooter.hidden = true);
      if (drawerCount) drawerCount.textContent = '';
      return;
    }
    cartEmpty && (cartEmpty.hidden = true);
    cartFooter && (cartFooter.hidden = false);
    if (drawerCount) drawerCount.textContent = cart.item_count;
    if (cartSubtotal) cartSubtotal.textContent = formatMoney(cart.total_price);

    updateShippingBar(cart.total_price);
    updateCartBadge(cart.item_count);

    cartItems.innerHTML = cart.items.map((item, idx) => `
      <div class="as-cart-item" data-index="${item.key}">
        <img
          class="as-cart-item__img"
          src="${item.image ? item.image.replace('.jpg', '_120x.jpg').replace('.png', '_120x.png') : ''}"
          alt="${item.title}"
          width="72" height="88"
          loading="lazy">
        <div class="as-cart-item__info">
          <div class="as-cart-item__name">${item.product_title}</div>
          ${item.variant_title && item.variant_title !== 'Default Title' ? `<div class="as-cart-item__variant">${item.variant_title}</div>` : ''}
          <div class="as-cart-item__qty">
            <button class="as-cart-item__qty-btn" data-action="decrease" data-key="${item.key}" data-qty="${item.quantity}" aria-label="Diminuer">−</button>
            <span class="as-cart-item__qty-val">${item.quantity}</span>
            <button class="as-cart-item__qty-btn" data-action="increase" data-key="${item.key}" data-qty="${item.quantity}" aria-label="Augmenter">+</button>
          </div>
          <button class="as-cart-item__remove" data-action="remove" data-key="${item.key}">Supprimer</button>
        </div>
        <div class="as-cart-item__price">${formatMoney(item.final_line_price)}</div>
      </div>
    `).join('');

    // Qty buttons
    $$('.as-cart-item__qty-btn, .as-cart-item__remove', cartItems).forEach(btn => {
      on(btn, 'click', async () => {
        const key = btn.dataset.key;
        const action = btn.dataset.action;
        let qty = parseInt(btn.dataset.qty || '1');
        if (action === 'increase') qty += 1;
        else if (action === 'decrease') qty -= 1;
        else if (action === 'remove') qty = 0;
        await updateCartItem(key, qty);
      });
    });
  }

  async function fetchAndRenderCart() {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      renderCartItems(cart);
    } catch (e) { console.warn('Cart fetch error', e); }
  }

  async function updateCartItem(key, quantity) {
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity })
      });
      const cart = await res.json();
      renderCartItems(cart);
    } catch (e) { console.warn('Cart update error', e); }
  }

  async function addToCart(formData) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(formData)
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.description || 'Erreur lors de l\'ajout');
    }
    return res.json();
  }

  // ATC global event listener (pour les quick-add des product cards)
  on(document, 'click', async (e) => {
    const btn = e.target.closest('[data-quick-add]');
    if (!btn) return;
    e.preventDefault();
    const variantId = btn.dataset.variantId;
    if (!variantId) return;
    btn.disabled = true;
    btn.innerHTML = '<svg width="14" height="14" style="animation:as-spin 0.6s linear infinite;border:2px solid rgba(0,0,0,0.2);border-top-color:#0A0A0A;border-radius:50%" viewBox="0 0 24 24"></svg>';
    try {
      await addToCart({ id: parseInt(variantId), quantity: 1 });
      const cart = await (await fetch('/cart.js')).json();
      updateCartBadge(cart.item_count);
      showToast('Produit ajouté au panier');
      openCartDrawer();
    } catch (err) {
      showToast(err.message, 4000);
    } finally {
      btn.disabled = false;
      btn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg> Ajouter';
    }
  });

  /* ============================================================
     PRODUCT PAGE — ATC
     ============================================================ */
  const pdpForm = $('#as-pdp-form');
  if (pdpForm) {
    on(pdpForm, 'submit', async (e) => {
      e.preventDefault();
      const btn = pdpForm.querySelector('[data-atc]');
      const btnText = btn && btn.querySelector('.as-btn__text');
      if (!btn) return;
      btn.classList.add('is-loading');
      try {
        const formData = new FormData(pdpForm);
        const id = formData.get('id');
        const qty = parseInt(formData.get('quantity') || '1');
        await addToCart({ id: parseInt(id), quantity: qty });
        const cart = await (await fetch('/cart.js')).json();
        updateCartBadge(cart.item_count);
        if (btnText) {
          const orig = btnText.textContent;
          btnText.textContent = '✓ Ajouté !';
          setTimeout(() => { btnText.textContent = orig; }, 2000);
        }
        setTimeout(() => openCartDrawer(), 300);
      } catch (err) {
        showToast(err.message, 4000);
      } finally {
        btn.classList.remove('is-loading');
      }
    });
  }

  /* ============================================================
     PRODUCT PAGE — VARIANTS
     ============================================================ */
  const variantSelect = $('#as-variant-select');
  if (variantSelect) {
    on(variantSelect, 'change', function () {
      const selected = this.options[this.selectedIndex];
      // Update price display
      const priceEl = $('#as-pdp-price');
      const oldPriceEl = $('#as-pdp-price-old');
      const price = selected.dataset.price;
      const comparePrice = selected.dataset.comparePrice;
      if (priceEl && price) priceEl.textContent = formatMoney(parseInt(price));
      if (oldPriceEl) {
        if (comparePrice && parseInt(comparePrice) > parseInt(price)) {
          oldPriceEl.textContent = formatMoney(parseInt(comparePrice));
          oldPriceEl.style.display = '';
        } else {
          oldPriceEl.style.display = 'none';
        }
      }
      // Update availability
      const avail = selected.dataset.available === 'true';
      const atcBtn = pdpForm && pdpForm.querySelector('[data-atc]');
      if (atcBtn) {
        atcBtn.disabled = !avail;
        const btnText = atcBtn.querySelector('.as-btn__text');
        if (btnText) btnText.textContent = avail ? 'Ajouter au panier' : 'Épuisé';
      }
      // Update gallery if image data present
      const featImg = selected.dataset.featuredImage;
      if (featImg) {
        const mainImg = $('#as-pdp-main-img');
        if (mainImg) mainImg.src = featImg;
      }
    });
  }

  /* ============================================================
     PRODUCT PAGE — VARIANT PILLS (alternative à <select>)
     ============================================================ */
  $$('.as-pdp__option-group').forEach(group => {
    const pills = $$('.as-pdp__option-pill', group);
    pills.forEach(pill => {
      on(pill, 'click', () => {
        if (pill.classList.contains('is-unavailable')) return;
        pills.forEach(p => p.classList.remove('is-selected'));
        pill.classList.add('is-selected');
        // Synchronise avec le <select> caché
        const optionPos = group.dataset.option;
        const nativeSelect = $(`#as-variant-select`);
        if (nativeSelect && optionPos) {
          const options = nativeSelect.options;
          // Reconstruit la clé de variante à partir de toutes les options sélectionnées
          const allSelected = $$('.as-pdp__option-group').map(g => {
            const sel = g.querySelector('.as-pdp__option-pill.is-selected');
            return sel ? sel.dataset.value : null;
          }).filter(Boolean);
          for (let opt of options) {
            const variantTitle = opt.value ? opt.dataset.title || '' : '';
            if (allSelected.every(v => variantTitle.includes(v))) {
              nativeSelect.value = opt.value;
              nativeSelect.dispatchEvent(new Event('change'));
              break;
            }
          }
        }
      });
    });
  });

  /* ============================================================
     PRODUCT PAGE — QUANTITÉ
     ============================================================ */
  $$('[data-qty-control]').forEach(ctrl => {
    const input = ctrl.closest('[data-qty-wrap]')
      ? ctrl.closest('[data-qty-wrap]').querySelector('[data-qty-input]')
      : null;
    if (!input) return;
    on(ctrl, 'click', () => {
      let val = parseInt(input.value || 1);
      if (ctrl.dataset.qtyControl === 'minus') val = Math.max(1, val - 1);
      else val += 1;
      input.value = val;
    });
  });

  /* ============================================================
     ACCORDÉONS
     ============================================================ */
  $$('.as-acc__head').forEach(head => {
    on(head, 'click', () => {
      const item = head.closest('.as-acc__item');
      const isOpen = item.classList.contains('is-open');
      // Ferme les autres dans le même groupe
      const group = head.closest('[data-acc-group]');
      if (group) {
        $$('.as-acc__item.is-open', group).forEach(i => i.classList.remove('is-open'));
      }
      if (!isOpen) item.classList.add('is-open');
    });
  });

  /* ============================================================
     TESTIMONIALS CAROUSEL
     ============================================================ */
  const tmTrack = $('#as-testi-track');
  if (tmTrack) {
    let tmIdx = 0;
    const tmDots = $$('.as-testi-dot');
    const tmCards = $$('.as-testi-card', tmTrack);
    let autoPlay;

    function goTo(idx) {
      const total = tmCards.length;
      tmIdx = (idx + total) % total;
      const cardW = tmCards[0].offsetWidth;
      const gap = 24;
      tmTrack.style.transform = `translateX(-${tmIdx * (cardW + gap)}px)`;
      tmDots.forEach((d, i) => d.classList.toggle('is-active', i === tmIdx));
    }

    const prevBtn = $('#as-testi-prev');
    const nextBtn = $('#as-testi-next');
    on(prevBtn, 'click', () => { clearInterval(autoPlay); goTo(tmIdx - 1); });
    on(nextBtn, 'click', () => { clearInterval(autoPlay); goTo(tmIdx + 1); });
    tmDots.forEach((dot, i) => on(dot, 'click', () => { clearInterval(autoPlay); goTo(i); }));

    autoPlay = setInterval(() => goTo(tmIdx + 1), 5500);
    on(tmTrack, 'mouseenter', () => clearInterval(autoPlay));
    on(tmTrack, 'mouseleave', () => { autoPlay = setInterval(() => goTo(tmIdx + 1), 5500); });

    // Touch swipe
    let touchStartX = 0;
    on(tmTrack, 'touchstart', e => { touchStartX = e.touches[0].clientX; }, { passive: true });
    on(tmTrack, 'touchend', e => {
      const dx = e.changedTouches[0].clientX - touchStartX;
      if (Math.abs(dx) > 50) { clearInterval(autoPlay); goTo(dx < 0 ? tmIdx + 1 : tmIdx - 1); }
    });
  }

  /* ============================================================
     SCROLL ANIMATIONS
     ============================================================ */
  const scrollEls = $$('.fade-up, .fade-in, .slide-left, .slide-right');
  if (scrollEls.length && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12 });
    scrollEls.forEach(el => io.observe(el));
  }


  /* ============================================================
     PARALLAX HERO (léger)
     ============================================================ */
  const heroVisual = $('#as-hero-visual');
  if (heroVisual) {
    on(window, 'scroll', () => {
      heroVisual.style.transform = `translateY(${window.scrollY * 0.25}px)`;
    }, { passive: true });
  }

  /* ============================================================
     COLLECTION — FILTRES ACTIFS PILLS
     ============================================================ */
  const filterPills = $$('.as-filter-pill');
  filterPills.forEach(pill => {
    on(pill, 'click', () => {
      filterPills.forEach(p => p.classList.remove('is-active'));
      pill.classList.add('is-active');
    });
  });

  /* ============================================================
     NEWSLETTER FORMS (Shopify Customer form)
     ============================================================ */
  $$('[data-nl-form]').forEach(form => {
    on(form, 'submit', async (e) => {
      // Le formulaire natif Shopify {% form 'customer' %} se soumet normalement
      // On gère juste l'UX de feedback
      const btn = form.querySelector('[data-nl-submit]');
      const input = form.querySelector('[data-nl-input]');
      if (btn) { btn.disabled = true; btn.textContent = '...'; }
      // Laisser le form se soumettre normalement (Shopify gère)
    });
  });

  /* ============================================================
     INIT CART BADGE au chargement
     ============================================================ */
  fetch('/cart.js')
    .then(r => r.json())
    .then(cart => { updateCartBadge(cart.item_count); })
    .catch(() => {});

  /* ============================================================
     GALERIE PRODUIT — THUMBNAILS
     ============================================================ */
  const thumbs = $$('.as-pdp__thumb');
  const mainImg = $('#as-pdp-main-img');
  thumbs.forEach(thumb => {
    on(thumb, 'click', () => {
      thumbs.forEach(t => t.classList.remove('is-active'));
      thumb.classList.add('is-active');
      if (mainImg) {
        const src = thumb.dataset.src;
        if (src) mainImg.src = src;
      }
    });
  });

})();
